import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle, ChevronDown } from 'lucide-react';

// Logo URLs - Using the white logo
import { BRAND } from '../config/brand';
const LOGO_WHITE = BRAND.logoWhite;

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const contactRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsContactOpen(false);
  }, [location]);

  // Close contact dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contactRef.current && !contactRef.current.contains(e.target)) {
        setIsContactOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Properties', path: '/properties' },
    { name: 'Compare with AI', path: '/compare' },
    { name: 'Loan Planner', path: '/emi-calculator' },
    { name: 'Enquire Now', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 w-full transition-all duration-300 ${
          isMobileMenuOpen ? 'z-[70]' : 'z-50'
        } ${
          isScrolled ? 'bg-obsidian/95 backdrop-blur-xl border-b border-white/5 py-2' : 'bg-transparent py-4'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between">
            {/* Logo - Big and visible */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src={LOGO_WHITE} 
                alt="Prop Assist - We Assist You Choose" 
                className="h-20 md:h-24 lg:h-28 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  onClick={() => { if (location.pathname === link.path) window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`text-base font-medium tracking-wide transition-colors ${
                    isActive(link.path) ? 'text-brand' : 'text-white/80 hover:text-brand'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA — Contact Us dropdown */}
            <div className="hidden lg:flex items-center">
              <div ref={contactRef} className="relative">
                <button
                  data-testid="contact-dropdown-btn"
                  onClick={() => setIsContactOpen(!isContactOpen)}
                  className="bg-brand-gradient text-white font-semibold px-5 py-2.5 rounded flex items-center gap-2 hover:shadow-lg hover:shadow-brand/20 transition-all text-sm uppercase tracking-wide"
                >
                  Contact Us
                  <ChevronDown size={16} className={`transition-transform ${isContactOpen ? 'rotate-180' : ''}`} />
                </button>
                {isContactOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-obsidian-card border border-white/10 rounded-lg shadow-2xl overflow-hidden">
                    <a
                      href="tel:+918459458536"
                      data-testid="dropdown-call-btn"
                      className="flex items-center gap-3 px-5 py-3.5 text-white/80 hover:bg-white/5 hover:text-brand transition-colors text-sm"
                    >
                      <Phone size={18} />
                      +91 84594 58536
                    </a>
                    <a
                      href="https://wa.me/918459458536"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="dropdown-whatsapp-btn"
                      className="flex items-center gap-3 px-5 py-3.5 text-white/80 hover:bg-white/5 hover:text-brand transition-colors text-sm border-t border-white/5"
                    >
                      <MessageCircle size={18} />
                      WhatsApp Us
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden text-white p-2 z-[70] relative"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Full screen overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu-overlay">
          {/* Solid dark overlay */}
          <div 
            className="absolute inset-0 bg-obsidian"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu content */}
          <div className="relative z-[70] pt-32 px-6">
            <div className="space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`block py-4 text-2xl font-medium border-b border-white/10 ${
                    isActive(link.path) ? 'text-brand' : 'text-white/80'
                  }`}
                  onClick={() => { setIsMobileMenuOpen(false); if (location.pathname === link.path) window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="mt-8 space-y-4">
              <a
                href="tel:+918459458536"
                className="flex items-center gap-3 text-white/80 text-lg py-2"
              >
                <Phone size={24} />
                +91 84594 58536
              </a>
              <a
                href="https://wa.me/918459458536"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-brand-gradient text-white font-semibold px-6 py-4 rounded flex items-center justify-center gap-2 w-full text-lg"
              >
                <MessageCircle size={24} />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
